"use client";

import React, { useState, useEffect } from "react";
import { supabase } from "../supabase/client";
import { Download, ImageIcon, Loader2, Filter, Layers } from "lucide-react";

export default function AssetGallery() {
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // all, main, extra, color

  const tabs = [
    { id: "all", label: "All Assets", icon: <Layers size={14} /> },
    { id: "main", label: "Main Images", icon: <ImageIcon size={14} /> },
    { id: "extra", label: "Gallery Extras", icon: <Filter size={14} /> },
    { id: "color", label: "Color Variants", icon: <div className="w-2 h-2 rounded-full bg-indigo-500" /> },
  ];

  useEffect(() => {
    fetchImages();
  }, []);

  async function fetchImages() {
    setLoading(true);
    try {
      const { data: folders, error: folderError } = await supabase.storage
        .from("cms-media")
        .list("products", { limit: 100 });

      if (folderError) throw folderError;

      const allProductData = [];

      for (const folder of folders) {
        if (folder.name === ".emptyFolderPlaceholder") continue;

        const { data: files, error: fileError } = await supabase.storage
          .from("cms-media")
          .list(`products/${folder.name}`);

        if (fileError) continue;

        const formattedFiles = files.map((file) => {
          const { data } = supabase.storage
            .from("cms-media")
            .getPublicUrl(`products/${folder.name}/${file.name}`);
          
          // Identify type for filtering
          let type = "other";
          if (file.name.startsWith("main-")) type = "main";
          if (file.name.startsWith("extra-")) type = "extra";
          if (file.name.startsWith("color-")) type = "color";

          return { name: file.name, url: data.publicUrl, type };
        });

        if (formattedFiles.length > 0) {
          allProductData.push({
            productName: folder.name,
            images: formattedFiles,
          });
        }
      }
      setGalleryData(allProductData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleDownload = async (url, fileName) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[400px]">
      <Loader2 className="animate-spin text-indigo-600 mb-2" />
      <p className="text-slate-400 text-sm">Organizing your library...</p>
    </div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto bg-slate-50 min-h-screen">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Media Explorer</h1>
        <p className="text-slate-500 text-sm">Browse and download product assets by category.</p>
      </header>

      {/* --- TABS --- */}
      <div className="flex gap-1 bg-slate-200/50 p-1 rounded-xl mb-10 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === tab.id 
                ? "bg-white text-indigo-600 shadow-sm" 
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* --- GALLERY SECTIONS --- */}
      <div className="space-y-12">
        {galleryData.map((product) => {
          // Filter images based on tab
          const filteredImages = product.images.filter(img => 
            activeTab === "all" || img.type === activeTab
          );

          if (filteredImages.length === 0) return null;

          return (
            <section key={product.productName} className="animate-in fade-in slide-in-from-bottom-4">
              <div className="flex items-center gap-3 mb-4 border-l-4 border-indigo-500 pl-4">
                <h3 className="font-bold text-slate-800 uppercase tracking-wider text-sm">
                  {product.productName.replace(/-/g, ' ')}
                </h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {filteredImages.map((img, idx) => (
                  <div key={idx} className="group relative aspect-[3/4] bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
                    <img src={img.url} className="w-full h-full object-cover" alt="" />
                    
                    {/* Badge for Type (Only shown in 'All' tab) */}
                    {activeTab === "all" && (
                      <div className="absolute top-2 left-2">
                         <span className={`text-[8px] px-2 py-0.5 rounded-full font-bold uppercase text-white shadow-md ${
                           img.type === 'main' ? 'bg-blue-500' : img.type === 'color' ? 'bg-indigo-500' : 'bg-slate-500'
                         }`}>
                           {img.type}
                         </span>
                      </div>
                    )}

                    <div className="absolute inset-0 bg-indigo-900/70 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center p-4">
                      <button
                        onClick={() => handleDownload(img.url, img.name)}
                        className="bg-white text-indigo-600 p-3 rounded-full hover:scale-110 transition-transform"
                      >
                        <Download size={20} />
                      </button>
                      <span className="mt-3 text-[10px] text-white/80 font-mono truncate w-full text-center">
                        {img.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}